// TypeScript mirror of grackle-core's chunk-plan math (core/src/upload.rs), adapted for a
// browser recording whose total length isn't known up front. Two layers:
//
//   1. Pure, network-free bookkeeping: `PartAccumulator` cuts MediaRecorder blobs into
//      upload-sized parts, `remainingPartNumbers` is the resume set-difference, `chunkBatches`
//      groups part numbers for the ≤100-per-call presign batch (§4 #16), `backoffDelayMs` is
//      the retry schedule. All five are exported and unit-testable with nothing but a Blob
//      (global in Node 18+) — no DOM, no fetch, no timers.
//   2. `UploadQueue`, the stateful orchestrator that talks to the API (§4 #15-18) and to the
//      presigned storage URLs it gets back. It is deliberately built on top of layer 1 rather
//      than mixing accumulation and network logic together, so recording never has to wait on
//      the network (FR-RD7/FR-P2): `push()` only ever touches the accumulator synchronously.
//
// Flow (exact, from build-plan-m1.md §1 WP11 + §4):
//   start()  -> POST /orgs/{org_id}/uploads                       {grackle_id, part_size}
//   push()   -> accumulate; when a part is cut, request its URL   POST /uploads/{id}/parts
//               and PUT the bytes straight to storage, retrying with backoff on failure
//   finish() -> flush the tail part, wait for every part to be accepted, then
//               POST /uploads/{id}/complete {total_bytes, parts:[{part_number, etag}]}
//   cancel() -> DELETE /uploads/{id}

import { api, ApiFail } from "../api/client";
import type { GrackleOut } from "../api/types";

/** §4 #15: the part size the server hands back on create is documented as the literal
 * 5242880 (5 MiB, S3's multipart minimum) — the same constant grackle-core uses. We size the
 * accumulator with this immediately, at construction, rather than waiting for the server's
 * response: recording must be able to buffer parts before the network has said anything back
 * (see `UploadQueue.push`). If a server ever returned a different value it would only affect
 * the number that gets echoed in the snapshot, not how bytes are actually sliced. */
export const DEFAULT_PART_SIZE = 5 * 1024 * 1024;

/** §4 #16: at most 100 part numbers per presigned-URL batch request. */
const MAX_PARTS_PER_BATCH = 100;

/** How many parts may be PUTting to storage at once. Keeps one slow/dropped connection from
 * stalling every other part, while not trying to blast the whole recording out in parallel. */
const UPLOAD_CONCURRENCY = 3;

/** Presigned part URLs expire in 15 minutes (§2.6). Treat one with less than this much life
 * left as unusable and fetch a replacement rather than racing the clock. */
const URL_SAFETY_MARGIN_MS = 60_000;

/** A presign batch that hasn't answered in this long is treated as a transient failure and
 * retried. Without a bound, one hung request held `requestingUrls` forever and no part ever got
 * a URL again — the recorder sat on "0 of N parts uploaded" for the rest of the recording. */
const PRESIGN_TIMEOUT_MS = 30_000;

/** Same for a part PUT: a hung connection otherwise holds one of the UPLOAD_CONCURRENCY slots
 * forever, and three of them stop the upload outright. 5 MiB in five minutes is ~140 kbit/s,
 * well under any uplink that can keep up with a live recording in the first place. */
const PART_PUT_TIMEOUT_MS = 5 * 60_000;

export interface AccumulatedPart {
  partNumber: number;
  blob: Blob;
  size: number;
}

/**
 * Buffers MediaRecorder blobs into upload-sized parts without ever touching the network —
 * pure bookkeeping over Blob objects (Blob.slice is synchronous and lazy, so cutting a part
 * never reads its bytes into memory by itself). Parts are numbered from 1, like grackle-core's
 * ChunkPlan and the S3 API both require.
 *
 * Like ChunkPlan::for_size, every part except the last is exactly partSize bytes — cut with
 * Blob.slice() across chunk boundaries exactly the way ChunkPlan slices a known-size file, so
 * a single MediaRecorder chunk that pushes the buffer far past one boundary can still yield
 * several full parts in one push() and never "borrows" bytes into an oversized part. The
 * difference from ChunkPlan is only that this works forward over a stream of unknown total
 * length instead of a known-size file: parts are cut as soon as partSize bytes are available,
 * and the final part (`flush()`) is whatever is left, however small.
 */
export class PartAccumulator {
  private readonly queue: Blob[] = [];
  private queuedBytes = 0;
  private nextPartNumber = 1;

  constructor(private readonly partSize: number = DEFAULT_PART_SIZE) {
    if (!Number.isFinite(partSize) || partSize <= 0) {
      throw new Error("partSize must be a positive number");
    }
  }

  /** Feed one MediaRecorder chunk (or any Blob). Returns zero or more parts now ready to
   * upload — more than one if this single chunk pushed the buffer past several part
   * boundaries at once (e.g. after resuming from a paused/slow tab). Zero-length chunks
   * (MediaRecorder emits one on a bare stop with nothing new to flush) are ignored. */
  push(chunk: Blob): AccumulatedPart[] {
    if (chunk.size === 0) return [];
    this.queue.push(chunk);
    this.queuedBytes += chunk.size;
    const parts: AccumulatedPart[] = [];
    while (this.queuedBytes >= this.partSize) {
      parts.push(this.cut(this.partSize));
    }
    return parts;
  }

  /** Cuts whatever remains as the final (possibly short) part. Returns null if nothing is
   * buffered — safe to call more than once, or after a push() that left nothing behind. */
  flush(): AccumulatedPart | null {
    if (this.queuedBytes === 0) return null;
    return this.cut(this.queuedBytes);
  }

  /** Bytes buffered but not yet cut into a part. */
  get bufferedBytes(): number {
    return this.queuedBytes;
  }

  /** Total parts ever cut (by push() or flush()), i.e. the highest part number issued. */
  get partsCut(): number {
    return this.nextPartNumber - 1;
  }

  private cut(size: number): AccumulatedPart {
    const pieces: Blob[] = [];
    let remaining = size;
    while (remaining > 0) {
      const head = this.queue[0];
      if (head.size <= remaining) {
        pieces.push(head);
        remaining -= head.size;
        this.queue.shift();
      } else {
        pieces.push(head.slice(0, remaining));
        this.queue[0] = head.slice(remaining);
        remaining = 0;
      }
    }
    this.queuedBytes -= size;
    const blob = pieces.length === 1 ? pieces[0] : new Blob(pieces);
    return { partNumber: this.nextPartNumber++, blob, size };
  }
}

/**
 * Resume is a set difference over accepted part numbers, never a cursor: a client that
 * retried out of order, or had several parts in flight when the network dropped, still
 * converges on exactly the parts the server doesn't have yet. Mirrors
 * `ChunkPlan::remaining` in core/src/upload.rs.
 */
export function remainingPartNumbers(
  cutPartNumbers: readonly number[],
  acceptedPartNumbers: ReadonlySet<number>,
): number[] {
  return cutPartNumbers.filter((n) => !acceptedPartNumbers.has(n));
}

/** Splits `items` into batches of at most `size`, preserving order — used for the
 * ≤100-part-numbers-per-call limit on POST /uploads/{id}/parts (§4 #16). */
export function chunkBatches<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) throw new Error("size must be positive");
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

/** Exponential backoff with jitter, capped at 30s. Shared by every retry loop below. Pure —
 * takes an attempt count, returns a delay; no timers, no globals. */
export function backoffDelayMs(attempt: number, baseMs = 800, capMs = 30_000): number {
  const exp = Math.min(capMs, baseMs * 2 ** Math.max(0, attempt));
  // Jitter in [0.7, 1.3) so many concurrent retries don't all land on the same tick.
  return Math.round(exp * (0.7 + Math.random() * 0.6));
}

/** 5xx/408/429 are worth retrying forever (the server or network is having a moment). Any
 * other ApiFail (422 validation, 409 conflict, 403 email_unverified, 404…) is the server
 * telling us this request is simply wrong — retrying it changes nothing, so we surface it as
 * a terminal failure instead of spinning forever. A thrown non-ApiFail (fetch itself failed —
 * offline, DNS, CORS) is always retryable. */
function isRetryable(err: unknown): boolean {
  if (err instanceof ApiFail) {
    return err.status >= 500 || err.status === 429 || err.status === 408;
  }
  return true;
}

function describeError(err: unknown): string {
  if (err instanceof ApiFail) return err.message;
  if (err instanceof Error) return err.message;
  return "upload failed";
}

/** The server's typed error code (e.g. `quota_exceeded`, FR-O10), when there is one. */
function describeErrorCode(err: unknown): string | null {
  return err instanceof ApiFail ? err.code : null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Rejects with a plain (retryable — see isRetryable) Error if `p` hasn't settled in `ms`. */
function withTimeout<T>(p: Promise<T>, ms: number, what: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${what} timed out`)), ms);
  });
  return Promise.race([p, timeout]).finally(() => clearTimeout(timer));
}

/** 404/410 on an `/uploads/{id}/*` route means the server-side upload is no longer open: the
 * grackle left `status = 'uploading'` (something already completed it) or the multipart session
 * is gone. No retry can ever succeed against it, so the queue fails terminally and says so,
 * instead of quietly re-asking for URLs it will never get. */
function isUploadGone(err: unknown): boolean {
  return err instanceof ApiFail && (err.status === 404 || err.status === 410);
}

/** Thrown for a part PUT the storage provider answered with 404 — S3's `NoSuchUpload`: the
 * multipart upload was completed or aborted, so this part can never be accepted. */
class PartUploadGone extends Error {}

export type UploadQueuePhase =
  | "idle" // constructed; start() not yet called
  | "starting" // POST /uploads in flight (retries with backoff on transient failure)
  | "uploading" // session created; parts flow up to storage as they're cut
  | "finishing" // finish() called: tail flushed, waiting on every part, then completing
  | "done" // complete() succeeded
  | "canceled" // cancel() called
  | "failed"; // a terminal (non-retryable) API error — session creation or complete() itself

export interface UploadQueueSnapshot {
  phase: UploadQueuePhase;
  grackleId: string | null;
  partSize: number;
  /** Total bytes handed to push() so far, whether uploaded yet or not — this is what
   * `recording continues even if uploads fail` looks like from the outside. */
  bytesRecorded: number;
  /** Sum of the size of parts the server has acknowledged (PUT returned 2xx). */
  bytesAccepted: number;
  partsCut: number;
  partsAccepted: number;
  /** True while some request (session creation, a URL batch, a part PUT, or complete) is
   * sitting in a backoff wait. The UI's cue for "still trying", never a hard error. */
  retrying: boolean;
  lastError: string | null;
  /** ApiFail.code of the terminal error, so the UI can say more than the message (F13a). */
  lastErrorCode: string | null;
}

interface UrlCacheEntry {
  url: string;
  expiresAt: number;
}

/** FR-RD14: one accepted part, as persisted by a `LocalSource` so a crashed/restarted upload
 * can skip parts the server already has rather than re-PUTting the whole recording. */
export interface UploadQueueCompletedPart {
  n: number;
  etag: string;
}

/** FR-RD14: resumes an upload session that already exists server-side — skips
 * `POST /orgs/{org}/uploads` and seeds already-accepted parts so replayed chunks that land on
 * an already-uploaded part boundary are recognized and never re-PUT. */
export interface UploadQueueResumeSeed {
  grackleId: string;
  partSize: number;
  completedParts: UploadQueueCompletedPart[];
}

export interface UploadQueueOptions {
  onSnapshot?: (snapshot: UploadQueueSnapshot) => void;
  meetUrl?: string;
  consentAcknowledged?: boolean;
  /** FR-RD14: fires once the upload session exists (a fresh `createSession()` only — not on
   * `resumeSeed`, whose caller already knows the id it supplied). Persist `{grackleId,
   * partSize}` here so a crash before the first part lands still has somewhere to resume. */
  onSessionCreated?: (grackleId: string, partSize: number) => void;
  /** FR-RD14: fires after each part PUT is accepted (2xx + ETag). Persist it so a resumed
   * upload can skip this part number. */
  onPartAccepted?: (part: UploadQueueCompletedPart) => void;
  /** FR-RD14: see `UploadQueueResumeSeed`. */
  resumeSeed?: UploadQueueResumeSeed;
  /** FR-P14: an uploaded file's size, known before the first byte is sent, so the storage
   * quota can refuse it at session creation instead of after the whole file has gone up. */
  sizeBytes?: number;
}

/**
 * Drives one recording's upload session end to end. Recording (via `push`) never blocks on
 * this class doing anything — every network call it makes is fire-and-forget from the
 * caller's point of view, retried in the background, and reflected only in the snapshot.
 */
export class UploadQueue {
  private readonly accumulator: PartAccumulator;
  private readonly readyParts = new Map<number, Blob>();
  private readonly urlCache = new Map<number, UrlCacheEntry>();
  private readonly accepted = new Map<number, string>(); // part_number -> etag
  private readonly inFlight = new Set<number>();
  private readonly attempts = new Map<number, number>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private readonly finishResolvers: {
    resolve: (grackle: GrackleOut) => void;
    reject: (err: unknown) => void;
  }[] = [];

  private phase: UploadQueuePhase = "idle";
  private grackleId: string | null = null;
  private partSize = DEFAULT_PART_SIZE;
  private bytesRecorded = 0;
  private bytesAccepted = 0;
  private retrying = false;
  private lastError: string | null = null;
  private lastErrorCode: string | null = null;
  private finishRequested = false;
  private completing = false;
  private requestingUrls = false;
  private urlFailures = 0;
  private canceled = false;

  constructor(
    private readonly orgId: string,
    private readonly title: string,
    /** A capture's `video/webm`/`video/mp4`, or an uploaded file's type (FR-P14). The server
     * holds the allowlist; this class just carries the string. */
    private readonly contentType: string,
    private readonly options: UploadQueueOptions = {},
  ) {
    const seed = options.resumeSeed;
    if (seed) {
      // FR-RD14: adopt the server-side session the caller already knows about instead of
      // creating a new one. `start()` below skips POST /orgs/{org}/uploads entirely.
      this.partSize = seed.partSize;
      this.grackleId = seed.grackleId;
      for (const part of seed.completedParts) this.accepted.set(part.n, part.etag);
      // Approximation: every part but the last is exactly partSize, and we don't retain each
      // resumed part's exact byte length, only its etag. This is a progress number only —
      // correctness (which parts to skip) is tracked by part number in `accepted`, not bytes.
      this.bytesAccepted = seed.completedParts.length * seed.partSize;
    }
    this.accumulator = new PartAccumulator(this.partSize);
  }

  snapshot(): UploadQueueSnapshot {
    return {
      phase: this.phase,
      grackleId: this.grackleId,
      partSize: this.partSize,
      bytesRecorded: this.bytesRecorded,
      bytesAccepted: this.bytesAccepted,
      partsCut: this.accumulator.partsCut,
      partsAccepted: this.accepted.size,
      retrying: this.retrying,
      lastError: this.lastError,
      lastErrorCode: this.lastErrorCode,
    };
  }

  /** Creates the upload session. Safe to call once; further chunks may be push()ed
   * immediately, even before this resolves — they buffer locally regardless. */
  start(): void {
    if (this.phase !== "idle") return;
    if (this.options.resumeSeed) {
      // FR-RD14: the session already exists (constructor seeded grackleId/accepted) — go
      // straight to uploading rather than POSTing a new one.
      this.phase = this.finishRequested ? "finishing" : "uploading";
      this.emit();
      this.pump();
      if (this.finishRequested) this.tryFinish();
      return;
    }
    this.phase = "starting";
    this.emit();
    void this.createSession();
  }

  /** Feed one MediaRecorder chunk. Purely local bookkeeping — never awaits the network, so
   * a stalled connection can never stall recording (FR-RD7/FR-P2). */
  push(chunk: Blob): void {
    if (this.canceled || chunk.size === 0) return;
    this.bytesRecorded += chunk.size;
    const parts = this.accumulator.push(chunk);
    for (const part of parts) {
      // FR-RD14: a resumed upload replays every chunk from local storage, which re-cuts the
      // same deterministic part boundaries — skip ones the server already has rather than
      // buffering (and re-PUTting) bytes it will just reject as redundant work.
      if (this.accepted.has(part.partNumber)) continue;
      this.readyParts.set(part.partNumber, part.blob);
    }
    if (parts.length > 0) {
      this.emit();
      this.pump();
    }
  }

  /** Call once recording has stopped and the last chunk has been push()ed. Flushes the tail
   * part, waits for every part (including the tail) to be accepted, then completes the
   * upload. Resolves with the completed grackle, or rejects if a terminal API error occurs. */
  finish(): Promise<GrackleOut> {
    this.finishRequested = true;
    const tail = this.accumulator.flush();
    if (tail && !this.accepted.has(tail.partNumber)) this.readyParts.set(tail.partNumber, tail.blob);

    if (this.phase === "failed" || this.canceled) {
      // Already terminal — there is no later event that would settle this promise.
      return Promise.reject(new Error(this.lastError ?? "upload stopped"));
    }
    const promise = new Promise<GrackleOut>((resolve, reject) => {
      this.finishResolvers.push({ resolve, reject });
    });

    if (this.grackleId) {
      this.phase = "finishing"; // failed/canceled already returned above
      this.emit();
      this.pump();
      this.tryFinish();
    } else {
      // Session creation is still retrying in the background; createSession() checks
      // finishRequested once it succeeds and moves straight into finishing.
      this.emit();
    }
    return promise;
  }

  /** Discards the recording: stops retrying, aborts the multipart upload server-side
   * (best-effort), and rejects any pending finish() call. */
  async cancel(): Promise<void> {
    if (this.canceled) return;
    this.canceled = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.phase = "canceled";
    this.emit();
    this.rejectAllFinish(new Error("upload canceled"));
    if (this.grackleId) {
      try {
        await api<void>("DELETE", `/uploads/${this.grackleId}`);
      } catch {
        // Best-effort — the recording is being discarded either way.
      }
    }
  }

  /** Stops all local activity — timers, retries, further requests, `complete` — WITHOUT
   * aborting the server-side upload (contrast `cancel()`). For a caller that is abandoning this
   * queue but not the recording: the recovery state machine giving up one attempt, so the next
   * attempt can resume the same upload instead of racing a queue left running in the background. */
  halt(): void {
    if (this.canceled) return;
    this.canceled = true;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.rejectAllFinish(new Error("upload halted"));
  }

  // ---- internals ----------------------------------------------------------------------

  /** A failure no retry can fix. Recording is unaffected (push() stays local and the chunks
   * are already on disk / in IndexedDB); the upload stops, the snapshot carries the reason, and
   * finish() rejects so the caller hands the session to recovery. */
  private failTerminal(err: unknown): void {
    if (this.phase === "failed" || this.canceled) return;
    const gone = isUploadGone(err) || err instanceof PartUploadGone;
    this.lastError = gone
      ? "The upload was closed on the server before every part arrived. This recording is " +
        "still saved on this device and will be uploaded again as a new recording."
      : describeError(err);
    this.lastErrorCode = gone ? "upload_gone" : describeErrorCode(err);
    this.phase = "failed";
    this.retrying = false;
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.emit();
    this.rejectAllFinish(err);
  }

  private schedulePump(delay: number): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      if (!this.canceled) this.pump();
    }, delay);
    this.timers.add(timer);
  }

  /** Ready parts with no usable presigned URL yet. */
  private partsNeedingUrls(now: number): number[] {
    return remainingPartNumbers([...this.readyParts.keys()], new Set(this.accepted.keys())).filter((n) => {
      const cached = this.urlCache.get(n);
      return !cached || cached.expiresAt <= now + URL_SAFETY_MARGIN_MS;
    });
  }

  private emit(): void {
    this.options.onSnapshot?.(this.snapshot());
  }

  private rejectAllFinish(err: unknown): void {
    for (const r of this.finishResolvers.splice(0)) r.reject(err);
    this.finishResolvers.length = 0;
  }

  private async createSession(): Promise<void> {
    let attempt = 0;
    while (!this.canceled) {
      try {
        const res = await api<{ grackle_id: string; part_size: number }>(
          "POST",
          `/orgs/${this.orgId}/uploads`,
          {
            title: this.title,
            content_type: this.contentType,
            ...(this.options.meetUrl ? { meet_url: this.options.meetUrl } : {}),
            ...(this.options.consentAcknowledged ? { consent_acknowledged: true } : {}),
            ...(this.options.sizeBytes !== undefined ? { size_bytes: this.options.sizeBytes } : {}),
          },
        );
        this.grackleId = res.grackle_id;
        this.partSize = res.part_size || DEFAULT_PART_SIZE;
        this.retrying = false;
        this.lastError = null;
        this.lastErrorCode = null;
        this.phase = this.finishRequested ? "finishing" : "uploading";
        this.emit();
        this.options.onSessionCreated?.(this.grackleId, this.partSize);
        this.pump();
        if (this.finishRequested) this.tryFinish();
        return;
      } catch (err) {
        this.lastError = describeError(err);
        this.lastErrorCode = describeErrorCode(err);
        if (!isRetryable(err)) {
          this.phase = "failed";
          this.retrying = false;
          this.emit();
          this.rejectAllFinish(err);
          return;
        }
        this.retrying = true;
        this.emit();
        attempt += 1;
        await sleep(backoffDelayMs(attempt));
      }
    }
  }

  /** Kicks off whatever network work is currently possible: fetching URLs for parts that
   * need them, and PUTting parts that already have one. No-op until a session exists. */
  private pump(): void {
    if (!this.grackleId || this.canceled || this.phase === "failed") return;
    void this.ensureUrls();
    this.drainUploads();
  }

  private async ensureUrls(): Promise<void> {
    if (this.requestingUrls || !this.grackleId) return;
    const need = this.partsNeedingUrls(Date.now());
    if (need.length === 0) return;

    this.requestingUrls = true;
    let batchFailed = false;
    try {
      for (const batch of chunkBatches(need, MAX_PARTS_PER_BATCH)) {
        let attempt = 0;
        for (;;) {
          if (this.canceled) return;
          try {
            const res = await withTimeout(
              api<{
                urls: { part_number: number; url: string; expires_at: string }[];
              }>("POST", `/uploads/${this.grackleId}/parts`, { part_numbers: batch }),
              PRESIGN_TIMEOUT_MS,
              "requesting upload URLs",
            );
            for (const u of res.urls) {
              this.urlCache.set(u.part_number, { url: u.url, expiresAt: Date.parse(u.expires_at) });
            }
            this.retrying = false;
            this.urlFailures = 0;
            break;
          } catch (err) {
            this.lastError = describeError(err);
            this.lastErrorCode = describeErrorCode(err);
            if (isUploadGone(err)) {
              this.failTerminal(err);
              return;
            }
            if (!isRetryable(err)) {
              // Some other 4xx on just this batch. Leave it for the backoff re-run scheduled
              // below rather than spinning on it here — but never silently: the snapshot
              // carries the error and `retrying` stays true until a request succeeds.
              this.retrying = true;
              batchFailed = true;
              break;
            }
            this.retrying = true;
            this.emit();
            attempt += 1;
            await sleep(backoffDelayMs(attempt));
          }
        }
      }
    } finally {
      this.requestingUrls = false;
      this.emit();
      this.drainUploads();
      // Parts cut while this pass was in flight — including the tail finish() flushes on Stop —
      // and parts in a batch that failed have no URL. Nothing else is guaranteed to call pump()
      // again (after Stop, no more parts get cut), so without this they stranded forever.
      if (!this.canceled && this.phase !== "failed") {
        const stillNeeded = this.partsNeedingUrls(Date.now());
        if (stillNeeded.length > 0) {
          const requested = new Set(need);
          if (!batchFailed && stillNeeded.some((n) => !requested.has(n))) {
            void this.ensureUrls();
          } else {
            this.urlFailures += 1;
            this.schedulePump(backoffDelayMs(this.urlFailures));
          }
        }
      }
    }
  }

  private drainUploads(): void {
    if (!this.grackleId || this.canceled || this.phase === "failed") return;
    for (const partNumber of this.readyParts.keys()) {
      if (this.accepted.has(partNumber) || this.inFlight.has(partNumber)) continue;
      if (this.inFlight.size >= UPLOAD_CONCURRENCY) break;
      if (!this.urlCache.has(partNumber)) continue;
      this.inFlight.add(partNumber);
      void this.attemptUpload(partNumber);
    }
  }

  private async attemptUpload(partNumber: number): Promise<void> {
    const blob = this.readyParts.get(partNumber);
    const cached = this.urlCache.get(partNumber);
    if (!blob || !cached) {
      this.inFlight.delete(partNumber);
      return;
    }
    try {
      // Direct PUT to the presigned storage URL — not the JSON api() client, and no
      // credentials: this call carries its own auth in the signed query string (§2.6) and
      // may be cross-origin against the storage provider.
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), PART_PUT_TIMEOUT_MS);
      let res: Response;
      try {
        res = await fetch(cached.url, { method: "PUT", body: blob, signal: abort.signal });
      } catch (err) {
        throw abort.signal.aborted ? new Error(`part ${partNumber} upload timed out`) : err;
      } finally {
        clearTimeout(timer);
      }
      if (res.status === 404) {
        throw new PartUploadGone(`part ${partNumber}: the upload no longer exists on the server`);
      }
      if (!res.ok) throw new Error(`part ${partNumber} upload failed with status ${res.status}`);
      // The ETag PUT returns is what complete() must echo back per part (§4 #17). Read it
      // here — it is never recoverable after this response is gone.
      const etag = res.headers.get("ETag") ?? res.headers.get("etag");
      if (!etag) {
        throw new Error(`part ${partNumber} upload response carried no ETag header`);
      }
      this.accepted.set(partNumber, etag);
      this.bytesAccepted += blob.size;
      this.readyParts.delete(partNumber);
      this.urlCache.delete(partNumber);
      this.attempts.delete(partNumber);
      this.inFlight.delete(partNumber);
      this.retrying = false;
      this.options.onPartAccepted?.({ n: partNumber, etag });
      this.emit();
      this.pump(); // frees a concurrency slot, and may be the last part finish() is waiting on
      if (this.finishRequested) this.tryFinish();
    } catch (err) {
      if (this.canceled) return;
      this.inFlight.delete(partNumber);
      if (err instanceof PartUploadGone) {
        this.failTerminal(err);
        return;
      }
      this.lastError = describeError(err);
      this.lastErrorCode = describeErrorCode(err);
      this.retrying = true;
      const attempt = (this.attempts.get(partNumber) ?? 0) + 1;
      this.attempts.set(partNumber, attempt);
      // Past the first failure, stop trusting the cached URL — an expired presigned
      // signature (15 min, §2.6) is the single most likely cause, and the only fix is a
      // fresh one, which the next ensureUrls() pass will fetch.
      if (attempt >= 2) this.urlCache.delete(partNumber);
      this.emit();
      this.schedulePump(backoffDelayMs(attempt));
    }
  }

  private tryFinish(): void {
    if (!this.finishRequested || !this.grackleId || this.completing || this.canceled) return;
    if (this.phase === "failed") return;
    const totalCut = this.accumulator.partsCut;
    for (let n = 1; n <= totalCut; n++) {
      if (!this.accepted.has(n)) return; // still waiting on at least one part
    }
    this.completing = true;
    void this.complete();
  }

  private async complete(): Promise<void> {
    const parts = [...this.accepted.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([part_number, etag]) => ({ part_number, etag }));
    let attempt = 0;
    while (!this.canceled) {
      try {
        const grackle = await api<GrackleOut>("POST", `/uploads/${this.grackleId}/complete`, {
          total_bytes: this.bytesRecorded,
          parts,
        });
        this.phase = "done";
        this.retrying = false;
        this.lastError = null;
        this.lastErrorCode = null;
        this.emit();
        for (const r of this.finishResolvers.splice(0)) r.resolve(grackle);
        return;
      } catch (err) {
        this.lastError = describeError(err);
        this.lastErrorCode = describeErrorCode(err);
        if (!isRetryable(err)) {
          this.phase = "failed";
          this.retrying = false;
          this.emit();
          this.rejectAllFinish(err);
          return;
        }
        this.retrying = true;
        this.emit();
        attempt += 1;
        await sleep(backoffDelayMs(attempt));
      }
    }
  }
}
